import type { FormField, JsonObject } from '../api/types';
import type { ManifestView } from '../microapp/manifest';
import { validateSubset, type FieldIssue } from './schema';
import { buildFieldValues, type NumberRange, type WidgetValue } from './StandardForm';

/**
 * 開始パネル（StartRunPanel）のための、processのinputSchemaの読み方。
 *
 * フォームにできるのは、トップレベルが type: object と properties で、各項目が
 * string / integer / number / boolean のどれかのときだけ。object・array・$ref・oneOf などが
 * 1つでもあればフォームをあきらめ、従来のJSON入力欄へ戻す。一部だけをフォームにすると
 * 画面から送れない項目が生まれるため。
 * 開始できるかの最終判定はサーバー（422 invalid_input）で、ここの検査は入力補助。
 */

/** processが宣言した開始入力のschema。 */
export interface DeclaredInputSchema {
  /** manifestのprocesses[].inputSchema。ZIP内の相対パスで、definition.schemasのキーと同じ形。 */
  path: string;
  /** definition.schemasに無ければnull。 */
  schema: JsonObject | null;
}

export type StartFieldType = 'string' | 'integer' | 'number' | 'boolean';

export interface StartField {
  /** 標準フォームと同じwidget語彙。labelはtitle、無ければプロパティ名。 */
  field: FormField;
  type: StartFieldType;
  required: boolean;
  description: string | undefined;
  /** number widgetのときだけ入る。 */
  range: NumberRange | undefined;
  /** schemaのdefault。指定が無ければundefined。 */
  defaultValue: unknown;
}

export interface StartInputForm {
  kind: 'form';
  schema: JsonObject;
  fields: StartField[];
}

export type StartInputPlan = StartInputForm | { kind: 'json'; reason: string };

/** maxLengthがこれを超える文字列はtextareaにする。 */
const TEXTAREA_MIN_LENGTH = 200;

/** 項目の形そのものを変えうるので、含まれていたらフォームにしないkeyword。 */
const COMPOSITION_KEYWORDS = [
  '$ref',
  '$dynamicRef',
  'allOf',
  'anyOf',
  'oneOf',
  'not',
  'if',
  'then',
  'else',
] as const;

/** 開始するprocessのinputSchemaを引く。宣言が無いprocessはnull（従来どおりのJSON入力欄）。 */
export function declaredInputSchema(
  manifest: ManifestView,
  schemas: Record<string, JsonObject>,
  processKey: string,
): DeclaredInputSchema | null {
  const path = manifest.processes.find((entry) => entry.key === processKey)?.inputSchema;
  // サーバー（start_input.py）と同じく、空の宣言は宣言なしとして扱う。
  if (!path) return null;
  return { path, schema: schemas[path] ?? null };
}

/** フォームにできるかを決め、できるなら項目を並べる。できなければJSON入力欄にする理由を返す。 */
export function planStartInput(declared: DeclaredInputSchema): StartInputPlan {
  const schema = declared.schema;
  if (schema === null) {
    return { kind: 'json', reason: `schema「${declared.path}」が定義に含まれていません` };
  }
  if (schema['type'] !== 'object') {
    return { kind: 'json', reason: 'トップレベルが type: object ではありません' };
  }
  // プロパティ名はschemaの作者が自由に付けられるので、キーの有無は in（prototypeも見る）ではなく
  // Object.hasOwn で判定する。
  const composedRoot = COMPOSITION_KEYWORDS.find((keyword) => Object.hasOwn(schema, keyword));
  if (composedRoot !== undefined) {
    return { kind: 'json', reason: `トップレベルに ${composedRoot} があります` };
  }
  const properties = asRecord(schema['properties']);
  if (properties === undefined) {
    return { kind: 'json', reason: 'properties がありません' };
  }

  const requiredKeys = Array.isArray(schema['required'])
    ? schema['required'].filter((key): key is string => typeof key === 'string')
    : [];
  // propertiesに無い必須キーはフォームから入れようがなく、必ず422になる。
  const unreachable = requiredKeys.find((key) => !Object.hasOwn(properties, key));
  if (unreachable !== undefined) {
    return { kind: 'json', reason: `必須項目「${unreachable}」が properties にありません` };
  }

  const fields: StartField[] = [];
  for (const [key, value] of Object.entries(properties)) {
    const property = asRecord(value);
    if (property === undefined) {
      return { kind: 'json', reason: `項目「${key}」の定義を読めません` };
    }
    const composed = COMPOSITION_KEYWORDS.find((keyword) => Object.hasOwn(property, keyword));
    if (composed !== undefined) {
      return { kind: 'json', reason: `項目「${key}」に ${composed} があります` };
    }
    const type = property['type'];
    if (!isFieldType(type)) {
      return {
        kind: 'json',
        reason: `項目「${key}」の型（${describeType(type)}）はフォームで扱えません`,
      };
    }
    fields.push(toStartField(key, property, type, requiredKeys.includes(key)));
  }
  return { kind: 'form', schema, fields };
}

export interface StartInputCheck {
  /** 画面に出すwidgetの値。まだ触っていない項目はschemaのdefault（無ければ空）。 */
  widgetValues: Record<string, WidgetValue>;
  /** 送る開始入力。任意項目の空欄はキーごと入れない。 */
  input: JsonObject;
  /** 送信前の検査結果。1件でもあれば開始しない。 */
  issues: FieldIssue[];
}

/** フォームの値から送る開始入力を組み、送信前の検査をする。 */
export function checkStartInput(
  form: StartInputForm,
  edited: Record<string, WidgetValue>,
): StartInputCheck {
  const widgetValues: Record<string, WidgetValue> = {};
  for (const start of form.fields) {
    const key = start.field.key;
    widgetValues[key] =
      (Object.hasOwn(edited, key) ? edited[key] : undefined) ?? initialWidgetValue(start);
  }
  const input = buildFieldValues(
    form.fields.map((start) => start.field),
    widgetValues,
    form.schema,
  );
  return {
    widgetValues,
    input,
    issues: validateSubset(form.schema, input, { pattern: true }),
  };
}

/**
 * JSON入力からフォームへ戻すときの値。フォームに無いキーと、widgetで表せない値のキーはdroppedへ出す。
 * JSONに無いキーはdefaultではなく空にする（JSONで消した項目を勝手に戻さない）。
 */
export function widgetValuesFromInput(
  form: StartInputForm,
  input: JsonObject,
): { values: Record<string, WidgetValue>; dropped: string[] } {
  const values: Record<string, WidgetValue> = {};
  const dropped: string[] = [];
  for (const { field } of form.fields) {
    const present = Object.hasOwn(input, field.key);
    const value = present ? toWidgetValue(field, input[field.key]) : undefined;
    if (present && value === undefined) dropped.push(field.key);
    values[field.key] = value ?? emptyWidgetValue(field);
  }
  for (const key of Object.keys(input)) {
    if (!form.fields.some(({ field }) => field.key === key)) dropped.push(key);
  }
  return { values, dropped };
}

/** フォームの初期値。schemaのdefaultがwidgetで表せればそれ、無ければ空。 */
function initialWidgetValue(start: StartField): WidgetValue {
  if (start.defaultValue === undefined) return emptyWidgetValue(start.field);
  return toWidgetValue(start.field, start.defaultValue) ?? emptyWidgetValue(start.field);
}

function toStartField(
  key: string,
  property: Record<string, unknown>,
  type: StartFieldType,
  required: boolean,
): StartField {
  const title = property['title'];
  const label = typeof title === 'string' && title.trim() !== '' ? title : key;
  const description = property['description'];
  const choices = property['enum'];
  let field: FormField;
  let range: NumberRange | undefined;

  if (type === 'boolean') {
    field = { key, label, widget: 'checkbox' };
  } else if (Array.isArray(choices)) {
    // 選択肢の表示名を持たせる拡張（x-enumLabelsのような）は契約に無いので、値そのものを出す。
    field = {
      key,
      label,
      widget: 'select',
      options: choices.map((choice: unknown) => ({
        label: typeof choice === 'string' ? choice : JSON.stringify(choice),
        value: choice,
      })),
    };
  } else if (type === 'integer' || type === 'number') {
    field = { key, label, widget: 'number' };
    range = {
      min: finiteNumber(property['minimum']),
      max: finiteNumber(property['maximum']),
      step: type === 'integer' ? 1 : 'any',
    };
  } else {
    const maxLength = property['maxLength'];
    const long = typeof maxLength === 'number' && maxLength > TEXTAREA_MIN_LENGTH;
    field = { key, label, widget: long ? 'textarea' : 'text' };
  }

  return {
    field,
    type,
    required,
    description: typeof description === 'string' && description !== '' ? description : undefined,
    range,
    defaultValue: property['default'],
  };
}

/** JSON値をwidgetの生の値へ寄せる。型違い・選択肢に無い値・object/array/nullはundefined。 */
function toWidgetValue(field: FormField, value: unknown): WidgetValue | undefined {
  switch (field.widget) {
    case 'checkbox':
      return typeof value === 'boolean' ? value : undefined;
    case 'select': {
      // DOMのvalue属性は文字列しか持てないので、StandardFormと同じくoptionの添字で持つ。
      const index = (field.options ?? []).findIndex((option) => option.value === value);
      return index < 0 ? undefined : String(index);
    }
    case 'number':
      return typeof value === 'number' && Number.isFinite(value) ? String(value) : undefined;
    case 'text':
    case 'textarea':
      return typeof value === 'string' ? value : undefined;
  }
}

function emptyWidgetValue(field: FormField): WidgetValue {
  return field.widget === 'checkbox' ? false : '';
}

function isFieldType(value: unknown): value is StartFieldType {
  return value === 'string' || value === 'integer' || value === 'number' || value === 'boolean';
}

function describeType(type: unknown): string {
  if (type === undefined) return '指定なし';
  return typeof type === 'string' ? type : JSON.stringify(type);
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}
